/**
 * list 命名空间字典（i18n 分区）
 *
 * 从原 translations.ts 主字典按 key 首个点分段拆出；本文件内 key 不带命名空间前缀，
 * 调用点写 t('list:key')。字段内容与拆分前逐字节一致。
 */

import type { TranslationDict } from '../types'

export const listZh: TranslationDict = {

  // ======================== 好友 / Friends ========================
  'title': '列表',
  'requests': '申请列表',
  'requestsTitle': '申请列表',
  'smartSort': '智能排序',
  'sortAlpha': '首字母',
  'sortRecent': '最近聊天',
  'sortAdded': '加好友时间',
  'noFriends': '列表里还没有人',
  'noFriendsHint': '在群聊中点击头像即可添加好友',
  'noSearchResults': '未找到匹配的用户',
  'tryOtherKeywords': '试试其他关键词',
  'search': '搜索用户',
  'searchPlaceholder': '搜索用户...',
  'searchButton': '搜索用户',
  'searchEmptyDesktop': '无匹配结果',
  'accept': '接受',
  'acceptButton': '接受',
  'reject': '拒绝',
  'rejectButton': '拒绝',
  'cancel': '撤回申请',
  'cancelRequest': '撤回申请',
  'human': '人类',
  'add': '添加好友',
  'tabFriends': '列表',
  'tabRequests': '申请列表',
  'friendAi': 'AI',
  'friendHuman': '人类',
  'receivedRequests': '收到的申请 ·',
  'sentRequests': '发出的申请 ·',
  'userPrefix': '用户',
  'noPendingRequests': '暂无待处理的申请',
  'pendingHint': '好友申请、入群申请与成员邀请审批都会显示在这里',
  'joinRequests': '入群申请 ·',
  'inviteApprovals': '邀请审批 ·',
  'applyToJoin': '申请加入',
  'invitedToJoin': '邀请加入',
  'approve': '通过',
  'deny': '驳回',
  'actionFailed': '操作失败，请重试',
  'defaultRequestMessage': '请求添加你为好友',
  'autoRespondWarning': '接受后将触发 AI 响应（消耗额度）',
  'sentRequestMessage': '你发送了好友申请',
  'autoRespondConfirm': '该 AI 已开启「自动回应好友申请」，接受后将自动触发其 API 响应（消耗额度）。是否继续？',
}

export const listEn: TranslationDict = {
  'title': 'List',
  'requests': 'Requests',
  'requestsTitle': 'Requests',
  'smartSort': 'Smart Sort',
  'sortAlpha': 'Alphabetical',
  'sortRecent': 'Recent Chat',
  'sortAdded': 'Date Added',
  'noFriends': 'Nobody here yet',
  'noFriendsHint': 'Click on avatars in group chat to add friends',
  'noSearchResults': 'No matching users found',
  'tryOtherKeywords': 'Try different keywords',
  'search': 'Search users',
  'searchPlaceholder': 'Search users...',
  'searchButton': 'Search users',
  'searchEmptyDesktop': 'No results',
  'accept': 'Accept',
  'acceptButton': 'Accept',
  'reject': 'Reject',
  'rejectButton': 'Reject',
  'cancel': 'Cancel Request',
  'cancelRequest': 'Cancel Request',
  'human': 'Human',
  'add': 'Add Friend',
  'tabFriends': 'List',
  'tabRequests': 'Requests',
  'friendAi': 'AI',
  'friendHuman': 'Human',
  'receivedRequests': 'Received Requests ·',
  'sentRequests': 'Sent Requests ·',
  'userPrefix': 'User',
  'noPendingRequests': 'No pending requests',
  'pendingHint': 'Friend requests, join requests and invite approvals all show up here',
  'joinRequests': 'Join Requests ·',
  'inviteApprovals': 'Invite Approvals ·',
  'applyToJoin': 'wants to join',
  'invitedToJoin': 'invites',
  'approve': 'Approve',
  'deny': 'Deny',
  'actionFailed': 'Action failed, please retry',
  'defaultRequestMessage': 'Requested to be your friend',
  'autoRespondWarning': 'Accepting will trigger AI response (costs credits)',
  'sentRequestMessage': 'You sent a friend request',
  'autoRespondConfirm': 'This AI has auto-respond enabled. Accepting will trigger its API response (costs credits). Continue?',
}

export const listJa: TranslationDict = {
  'title': 'リスト',
  'requests': '申請一覧',
  'requestsTitle': '申請一覧',
  'smartSort': 'スマート並び替え',
  'sortAlpha': 'アルファベット順',
  'sortRecent': '最近のチャット',
  'sortAdded': '追加日時',
  'noFriends': 'まだ誰もいません',
  'noFriendsHint': 'グループチャットでアイコンをクリックしてフレンド追加',
  'noSearchResults': '一致するユーザーが見つかりません',
  'tryOtherKeywords': '他のキーワードをお試しください',
  'search': 'ユーザーを検索',
  'searchPlaceholder': 'ユーザーを検索...',
  'searchButton': 'ユーザーを検索',
  'searchEmptyDesktop': '該当なし',
  'accept': '承認',
  'acceptButton': '承認',
  'reject': '拒否',
  'rejectButton': '拒否',
  'cancel': '申請を取り消す',
  'cancelRequest': '申請を取り消す',
  'human': '人間',
  'add': 'フレンド追加',
  'tabFriends': 'リスト',
  'tabRequests': '申請一覧',
  'friendAi': 'AI',
  'friendHuman': '人間',
  'receivedRequests': '受信した申請 ·',
  'sentRequests': '送信した申請 ·',
  'userPrefix': 'ユーザー',
  'noPendingRequests': '保留中の申請はありません',
  'pendingHint': 'フレンド申請・参加申請・招待の承認はここに表示されます',
  'joinRequests': '参加申請 ·',
  'inviteApprovals': '招待の承認 ·',
  'applyToJoin': '参加を希望',
  'invitedToJoin': 'が招待',
  'approve': '承認',
  'deny': '却下',
  'actionFailed': '操作に失敗しました。もう一度お試しください',
  'defaultRequestMessage': 'フレンド申請を送信しました',
  'autoRespondWarning': '承認するとAIが応答します（クレジットを消費します）',
  'sentRequestMessage': 'あなたがフレンド申請を送信しました',
  'autoRespondConfirm': 'このAIは自動応答が有効です。承認するとAPI応答がトリガーされます（クレジットを消費します）。続行しますか？',
}
